import { formatFilterCountSuffix } from "@/marketplace/count-active-filters";
import {
    ACTION_BAR_VALUE_FACET_ATTRIBUTES,
    resolveActionBarFacetActiveCount,
    resolveActionBarFacetActiveValues,
    type FacetRefinementState,
} from "@/marketplace/action-bar-facet-active-counts";
import { MAKE_DEPENDENT_REFINEMENT_FACETS } from "@/services/algoliaRouting.blade";

/** Ce que la croix utilise de l'helper de l'index InstantSearch. */
export interface FacetClearHelper {
    state: FacetRefinementState;
    clearRefinements(attribute: string): FacetClearHelper;
    removeDisjunctiveFacetRefinement(attribute: string, value: string): FacetClearHelper;
    removeFacetRefinement(attribute: string, value: string): FacetClearHelper;
    search(): unknown;
}

/** « Aston Martin », ou « Aston Martin +1 » avec plusieurs valeurs ; null sans valeur. */
export function formatFacetValueLabel(values: string[]): string | null {
    if (values.length === 0) return null;

    return values.length === 1
        ? values[0]
        : `${values[0]} +${values.length - 1}`;
}

function setFacetDropdownTriggerState(
    dropdown: HTMLElement,
    trigger: HTMLButtonElement,
    defaultLabel: string,
    activeCount: number,
    valueLabel: string | null,
    clearable: boolean,
): void {
    const label = valueLabel ?? defaultLabel + formatFilterCountSuffix(activeCount);
    const labelEl = trigger.querySelector<HTMLElement>(
        "[data-facet-trigger-label]",
    );
    if (labelEl) {
        labelEl.textContent = label;
    } else {
        const caret = trigger.querySelector(".marketplace-facet-dropdown__caret");
        trigger.textContent = "";
        trigger.append(`${label} `);
        if (caret) trigger.appendChild(caret);
    }

    // La pastille affiche la valeur : le nom de la facette reste lu par les lecteurs d'écran
    if (valueLabel) {
        trigger.setAttribute("aria-label", `${defaultLabel} : ${valueLabel}`);
    } else {
        trigger.removeAttribute("aria-label");
    }

    trigger.classList.toggle(
        "marketplace-action-bar__btn--primary",
        activeCount > 0,
    );

    dropdown.classList.toggle("marketplace-facet-dropdown--clearable", clearable);
    const clearButton = dropdown.querySelector<HTMLButtonElement>(
        "[data-facet-clear]",
    );
    if (clearButton) clearButton.hidden = !clearable;
}

/**
 * Sync action-bar facet dropdown triggers: selected values (or label + count) and active styling.
 * `state` = état de recherche en direct de la marketplace ; sans lui, les valeurs viennent de la page.
 */
export function updateActionBarFacetDropdownLabels(
    state?: FacetRefinementState | null,
): void {
    if (typeof document === "undefined") return;

    document
        .querySelectorAll<HTMLElement>("[data-facet-dropdown]")
        .forEach((dropdown) => {
            const mountId = dropdown.dataset.facetMount;
            const defaultLabel = dropdown.dataset.facetLabel ?? "";
            const trigger = dropdown.querySelector<HTMLButtonElement>(
                "[data-facet-dropdown-trigger]",
            );
            if (!trigger || !mountId) return;

            // Barre de la home : toute interaction renvoie vers la marketplace, on garde « Marque (1) »
            const isValueFacet =
                mountId in ACTION_BAR_VALUE_FACET_ATTRIBUTES &&
                !dropdown.closest("#hero-filter-bar");

            if (!isValueFacet) {
                setFacetDropdownTriggerState(
                    dropdown,
                    trigger,
                    defaultLabel,
                    resolveActionBarFacetActiveCount(mountId),
                    null,
                    false,
                );
                return;
            }

            const { values, removableValues } =
                resolveActionBarFacetActiveValues(mountId, state);

            setFacetDropdownTriggerState(
                dropdown,
                trigger,
                defaultLabel,
                values.length,
                formatFacetValueLabel(values),
                removableValues.length > 0,
            );
        });
}

/**
 * Retire les valeurs choisies par le visiteur (jamais celles de la page). La marque passe par son
 * widget, qui vide aussi les facettes qui en dépendent (modèle, options, couleurs…).
 */
function clearActionBarFacet(mountId: string, helper: FacetClearHelper): void {
    const attribute = ACTION_BAR_VALUE_FACET_ATTRIBUTES[mountId];
    if (!attribute) return;

    if (attribute === "make") {
        const pinnedMakeClear = document
            .getElementById(mountId)
            ?.querySelector<HTMLInputElement>("[data-make-pinned] .RefinementList__clear");
        if (pinnedMakeClear) {
            pinnedMakeClear.click();
            return;
        }

        helper.clearRefinements("make");
        MAKE_DEPENDENT_REFINEMENT_FACETS.forEach((facet) => helper.clearRefinements(facet));
        helper.search();
        return;
    }

    const { removableValues } = resolveActionBarFacetActiveValues(mountId, helper.state);
    if (removableValues.length === 0) return;

    const disjunctive = helper.state.disjunctiveFacetsRefinements?.[attribute] ?? [];
    removableValues.forEach((value) => {
        if (disjunctive.includes(value)) {
            helper.removeDisjunctiveFacetRefinement(attribute, value);
        } else {
            helper.removeFacetRefinement(attribute, value);
        }
    });
    helper.search();
}

let clearButtonsBound = false;

/** Croix des pastilles actives (« Aston Martin × ») : retire le filtre sans ouvrir le panneau. */
export function initActionBarFacetClearButtons(
    getHelper: () => FacetClearHelper | null | undefined,
): void {
    if (clearButtonsBound || typeof document === "undefined") return;
    clearButtonsBound = true;

    document.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const button = target.closest<HTMLButtonElement>("[data-facet-clear]");
        if (!button) return;

        event.preventDefault();
        const mountId = button.closest<HTMLElement>("[data-facet-dropdown]")
            ?.dataset.facetMount;
        const helper = getHelper();
        if (mountId && helper) clearActionBarFacet(mountId, helper);
    });
}
